import { useState, useCallback } from 'react';
import { useTranslation } from '@hotjar/translations';
import { useCurrentSite } from '@hotjar/site';
import { useNotify } from '@hotjar/hotsauce';
import * as Sentry from '@sentry/browser';
import { refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { OVERVIEW } from '@hotjar/webapp/constants/routes';
import { DEMO_SITE_ID } from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import { addUserToDemoSite } from './api';

export interface AddToDemoSite {
  addToDemoSite: (url?: string, options?: Record<string, string | number>) => Promise<void>;
  isLoading: boolean;
}

export const useAddToDemoSite = (): AddToDemoSite => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { showToast } = useNotify();

  const addToDemoSite = useCallback(
    async (url?: string, options?: Record<string, string | number>) => {
      try {
        setIsLoading(true);
        await addUserToDemoSite();
        await refreshFromServer();
        localStorage.setItem('lastVisitedSiteId', JSON.stringify(currentSite?.id));
        setIsLoading(false);
        window.location.assign(
          getRoute(url || OVERVIEW.BASE.path, { siteId: DEMO_SITE_ID, ...(options || {}) }),
        );
        window.scrollTo(0, 0); // we need this since router doesn't understand it's a different route
      } catch (error) {
        setIsLoading(false);
        Sentry.captureException(error);
        showToast({
          text: t(`activation:space-overview.product-tour.error`),
          kind: 'danger',
        });
      }
    },
    [currentSite?.id, showToast, t],
  );

  return {
    isLoading,
    addToDemoSite,
  };
};
